/**
 * A lazy chunk (`import()`) that failed to load. Two very different causes
 * share this one symptom:
 *
 * - DEPLOY SKEW — the tab was opened before a deploy and asks for a hashed file
 *   the new deployment no longer serves (404);
 * - TRANSPORT — the file exists, but the download died (flaky mobile network,
 *   iOS suspending a backgrounded tab mid-request).
 *
 * Measured 2026-10-16: the chunk named in FLASHKA-VITE-1QJ (4.5k events/week)
 * was being served 200 by the current deployment, so most of these are
 * transport, not skew. Either way a reload is the cure — the browser caches a
 * failed module import for the life of the document, so retrying `import()`
 * in place cannot succeed — and the app already reloads (ErrorPage) or prompts
 * to (the update toast). That makes a chunk failure a RECOVERED condition,
 * not an error: it is counted in analytics and reported to Sentry only when
 * the reload did not fix it (see {@link shouldAutoReloadForChunkError}), or
 * when the deployment this page was built for no longer serves the chunk
 * (see {@link shouldReportMissingChunk}).
 */

/** One message per engine, plus Vite's own. Matched by prefix/substring. */
const CHUNK_LOAD_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'is not a valid JavaScript MIME type', // an HTML fallback served for a .js URL
  'Unable to preload CSS for', // Vite's preload helper
];

/**
 * A chunk whose download was cut short parses as a truncated script: WebKit
 * says exactly these, with no stack (FLASHKA-VITE-2T5, -2S — 2T5 caught at the
 * app root from a `Lazy` import). Whole-message match on a SyntaxError only,
 * so a JSON parse error ("JSON Parse error: Unexpected EOF") or a real syntax
 * error in our code never matches.
 */
const TRUNCATED_CHUNK_MESSAGES = ['Unexpected EOF', 'Unexpected end of script'];

export function isChunkLoadError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { message, name } = err as { message?: unknown; name?: unknown };
  if (typeof message !== 'string') return false;
  if (name === 'SyntaxError' && TRUNCATED_CHUNK_MESSAGES.includes(message)) return true;
  return CHUNK_LOAD_MESSAGES.some((m) => message.includes(m));
}

/** The chunk URL, when the engine names it (Chrome and Firefox do, Safari does not). */
export function chunkUrlFromError(err: unknown): string | null {
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message !== 'string') return null;
  const match = /(https?:\/\/\S+?\.(?:js|css))\b/.exec(message);
  return match ? match[1] : null;
}

/**
 * Whether the chunk is missing from the deployment this tab talks to (skew) or
 * still served (transport). A HEAD request — no body — that bypasses caches.
 */
export async function classifyChunkFailure(
  url: string | null,
): Promise<'missing' | 'transient' | 'unknown'> {
  if (!url) return 'unknown';
  try {
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (res.status === 404) return 'missing';
    return res.ok ? 'transient' : 'unknown';
  } catch {
    // Still offline — that IS the transport case.
    return 'transient';
  }
}

const LAST_AUTO_RELOAD_KEY = 'flashka:chunk-error-reload-at';
const RELOAD_PROBE_KEY = 'flashka:chunk-error-reload-probe';
/** A second failure within this window means the reload did not cure it. */
export const AUTO_RELOAD_WINDOW_MS = 60_000;

/** The last auto-reload, 0 for none — or null when the mark cannot be kept:
 *  sessionStorage throws on read, or on write (blocked, full). */
function readLastReload(): number | null {
  try {
    const storage = window.sessionStorage;
    const last = storage.getItem(LAST_AUTO_RELOAD_KEY);
    // Prove a mark can be written too (a timestamp-sized value under a probe
    // key — the real mark is only written right before the reload), or the
    // reload it guards would never be seen by the next page load.
    storage.setItem(RELOAD_PROBE_KEY, String(Date.now()));
    storage.removeItem(RELOAD_PROBE_KEY);
    return Number(last) || 0;
  } catch {
    return null;
  }
}

/**
 * True when reloading is the right answer to a chunk failure: we have not
 * already reloaded for one in the last minute. The guard is what stops a chunk
 * that is broken for real (a bad deploy) from reloading the tab forever — and
 * that second failure is the one worth reporting. With no storage for the
 * mark the guard cannot work, so there is no auto-reload at all: the page
 * keeps its buttons (which load the page) and the boundary reports it —
 * instead of a reload every 3 s, forever, that nobody hears about.
 */
export function shouldAutoReloadForChunkError(now = Date.now()): boolean {
  const last = readLastReload();
  return last !== null && now - last > AUTO_RELOAD_WINDOW_MS;
}

/** Record the reload BEFORE doing it, so the next page load can see it. */
export function markChunkErrorAutoReload(now = Date.now()): void {
  try {
    window.sessionStorage.setItem(LAST_AUTO_RELOAD_KEY, String(now));
  } catch {
    // Unreachable in practice: `shouldAutoReloadForChunkError` has just
    // proven the write works, and says no reload when it does not.
  }
}

const REPORTED_PERSISTENT_KEY = 'flashka:chunk-persistent-reported';
let reportedPersistentThisPage = false;

/**
 * Whether the boundary should send THIS persistent chunk failure to Sentry:
 * the first one in the tab session only. A tab the reload did not cure keeps
 * failing — every page load, and every navigation (the boundary resets on a
 * path change and the next lazy route rejects again) — and each was its own
 * report: one iPad sent four in seven seconds (FLASHKA-VITE-122, 2026-10-19).
 * One report per tab carries the whole signal; Sentry counts the users.
 * Safari names no chunk, so this is per tab, not per chunk.
 */
export function claimPersistentChunkReport(): boolean {
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(REPORTED_PERSISTENT_KEY)) return false;
    storage.setItem(REPORTED_PERSISTENT_KEY, String(Date.now()));
    return true;
  } catch {
    // Storage blocked: once per page load is still a rate limit.
    if (reportedPersistentThisPage) return false;
    reportedPersistentThisPage = true;
    return true;
  }
}

const REPORTED_MISSING_KEY = 'flashka:chunk-missing-reported';
const reportedMissingThisPage = new Set<string>();

function claimMissingChunkReport(chunk: string): boolean {
  if (reportedMissingThisPage.has(chunk)) return false;
  reportedMissingThisPage.add(chunk);
  try {
    const seen = JSON.parse(window.sessionStorage.getItem(REPORTED_MISSING_KEY) ?? '[]');
    const list: string[] = Array.isArray(seen) ? seen : [];
    if (list.includes(chunk)) return false;
    window.sessionStorage.setItem(
      REPORTED_MISSING_KEY,
      JSON.stringify([...list, chunk].slice(-20)),
    );
  } catch {
    // Storage blocked: once per page load is still a rate limit.
  }
  return true;
}

/**
 * Whether a chunk that 404s is a broken deploy worth one Sentry report, not
 * skew. Skew is a tab whose HTML is older than the deployment it now talks to —
 * the update toast handles it. When the deployment serving `/version.json` IS
 * the build this page runs (`deployedBuildTime === ownBuildTime`), nothing is
 * stale: the current deployment references a chunk it does not serve. That is
 * the failure the boundary's `persistent` report misses whenever the lazy
 * import sits under a `fallback={null}` boundary or a handler, so it never
 * reaches the app-root boundary or its reload. Reported at most once per chunk
 * per tab session. A `version.json` that could not be read (`null`) counts as
 * skew and reports nothing.
 */
export function shouldReportMissingChunk(
  kind: 'missing' | 'transient' | 'unknown',
  chunk: string | null,
  deployedBuildTime: number | null,
  ownBuildTime: number,
): boolean {
  if (kind !== 'missing' || !chunk) return false;
  if (deployedBuildTime === null || deployedBuildTime !== ownBuildTime) return false;
  return claimMissingChunkReport(chunk);
}

/** What a persistent chunk report says about the files the page never got. */
export type UnloadedChunk = {
  chunk: string;
  server: 'missing' | 'transient' | 'unknown';
};

/** A HEAD that has not answered by then is reported as unknown, not waited on. */
const DIAGNOSE_TIMEOUT_MS = 3_000;

/**
 * WHICH chunk a persistent failure is about — Safari's message names none
 * (FLASHKA-VITE-122: every report since #799 is desktop Safari, failing again
 * within 0.3 s of the reload). Vite inserts a `<link rel="modulepreload">` for
 * every dependency of a lazy import before importing it, and the browser makes
 * a resource-timing entry for every file it actually received. A preloaded
 * `/assets/` file with no received body is the one that broke the import.
 *
 * Each is then asked of the server: `missing` (404) is a broken deploy;
 * `transient` (served 200) means THIS browser refused a file the server has —
 * a content blocker matching the file's name, or a corrupt cache entry.
 */
export async function diagnoseUnloadedChunks(
  doc: Pick<Document, 'querySelectorAll'> = document,
  perf: Pick<Performance, 'getEntriesByType'> = performance,
): Promise<UnloadedChunk[]> {
  const received = new Set(
    (perf.getEntriesByType('resource') as PerformanceResourceTiming[])
      .filter((entry) => entry.decodedBodySize > 0 || entry.transferSize > 0)
      .map((entry) => entry.name),
  );
  const unloaded = [
    ...new Set(
      [...doc.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"]')]
        .map((link) => link.href)
        .filter((href) => /\/assets\/.+\.js(\?|$)/.test(href) && !received.has(href)),
    ),
  ].slice(0, 10);
  return Promise.all(
    unloaded.map(async (url) => ({
      chunk: url.replace(/^.*\/assets\//, 'assets/'),
      server: await Promise.race([
        classifyChunkFailure(url),
        new Promise<'unknown'>((resolve) =>
          setTimeout(() => resolve('unknown'), DIAGNOSE_TIMEOUT_MS),
        ),
      ]),
    })),
  );
}

/** The browser asked for a script and got a web page back. */
const HTML_FOR_SCRIPT_MESSAGE = 'is not a valid JavaScript MIME type';

/**
 * Whether a chunk failure that a reload did not cure comes from an OLD copy of
 * Flashka still in this browser, so the error page can say so and how to get
 * the current one (refresh, clear the cache, another browser) instead of
 * "something went wrong". Only what proves it counts:
 *
 * - the server deploys a NEWER build than this page (`version.json`);
 * - a file this page needs is gone from the server (404), and the server is not
 *   running this very build: the same build missing a file is a broken deploy
 *   ({@link shouldReportMissingChunk}), not an old page. An unreadable
 *   `version.json` (`null`) cannot be the same build, so it counts as old;
 * - the browser got a web page where it asked for a script — a stored HTML
 *   answer under a script URL (the cached-asset incident of July 2026).
 *
 * A file the server still serves (`transient`) or a check that could not run
 * (`unknown`) proves nothing about the version, so it keeps the general copy.
 */
export function isStaleVersionFailure({
  error,
  deployedBuildTime,
  ownBuildTime,
  unloadedChunks,
}: {
  error: unknown;
  deployedBuildTime: number | null;
  ownBuildTime: number;
  unloadedChunks: readonly UnloadedChunk[];
}): boolean {
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message.includes(HTML_FOR_SCRIPT_MESSAGE))
    return true;
  if (deployedBuildTime !== null && deployedBuildTime > ownBuildTime) return true;
  return (
    deployedBuildTime !== ownBuildTime &&
    unloadedChunks.some((c) => c.server === 'missing')
  );
}
